import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import type {
  CreateRepairServiceTypeRequest,
  RepairDeviceType,
  RepairServiceType,
  UpdateRepairServiceTypeRequest,
  Vendor,
} from '@techgitter/contracts';
import {
  createRepairServiceType,
  deleteRepairServiceType,
  fetchRepairServiceTypes,
  updateRepairServiceType,
} from '../services/repairServiceTypesApi';
import {
  invalidateRepairServiceTypesCache,
} from '../hooks/useRepairServiceTypes';
import { useProductCatalog } from '../hooks/useProductCatalog';
import { getVendors } from '../services/vendorService';
import { RepairServicePanel } from '../components/admin/RepairServicePanel';

const DEVICE_TYPE_LABEL: Record<NonNullable<RepairDeviceType>, string> = {
  laptop: 'Laptop',
  desktop: 'Desktop',
  all_in_one: 'All-in-one',
};

function formatDeviceType(value: RepairDeviceType): string {
  if (!value) return 'Any';
  return DEVICE_TYPE_LABEL[value] ?? value;
}

export const RepairServicesAdminPage: React.FC = () => {
  const [items, setItems] = useState<RepairServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [editing, setEditing] = useState<RepairServiceType | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<RepairServiceType | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { products, isLoading: productsLoading } = useProductCatalog();

  // Vendors are used by the third-party picker in the edit dialog. We
  // load them alongside the repair-service-type list so the dialog can
  // open instantly once data lands. Errors from this fetch are non-fatal
  // — the dialog gracefully shows an empty vendor list.
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setVendorsLoading(true);
    getVendors(controller.signal)
      .then((data) => setVendors(data))
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        // Non-fatal — surface to console; the third-party picker just
        // won't show any options until vendors load.
        // eslint-disable-next-line no-console
        console.warn('Failed to load vendors for repair-service admin:', err);
      })
      .finally(() => setVendorsLoading(false));
    return () => controller.abort();
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await fetchRepairServiceTypes(signal, {
        includeInactive: true,
      });
      setItems(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setLoadError(
        err instanceof Error ? err.message : 'Failed to load repair services',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const dialogOpen = creating || editing !== null;

  const closeDialog = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
    setSaveError(null);
  };

  const handleSave = async (
    payload: CreateRepairServiceTypeRequest | UpdateRepairServiceTypeRequest,
    id: string | null,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (id === null) {
        const created = await createRepairServiceType(
          payload as CreateRepairServiceTypeRequest,
        );
        setItems((current) => [...current, created]);
        setSnackbar({
          open: true,
          message: 'Repair service created',
          severity: 'success',
        });
      } else {
        const updated = await updateRepairServiceType(
          id,
          payload as UpdateRepairServiceTypeRequest,
        );
        setItems((current) =>
          current.map((it) => (it.id === updated.id ? updated : it)),
        );
        setSnackbar({
          open: true,
          message: 'Repair service updated',
          severity: 'success',
        });
      }
      // Bust the shared catalog cache so the quote picker sees the change
      // immediately — without this, the new/updated service stays invisible
      // to live quote editors until the page is reloaded.
      invalidateRepairServiceTypesCache();
      setCreating(false);
      setEditing(null);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save repair service',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await deleteRepairServiceType(deleting.id);
      setItems((current) => current.filter((it) => it.id !== deleting.id));
      invalidateRepairServiceTypesCache();
      setSnackbar({
        open: true,
        message: 'Repair service deleted',
        severity: 'success',
      });
      setDeleting(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : 'Failed to delete repair service',
        severity: 'error',
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const sortedItems = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const filtered = query
      ? items.filter((item) => {
          const haystack = [
            item.repairServiceCode,
            item.displayName,
            item.serviceType,
            formatDeviceType(item.deviceType),
            ...item.componentCategories,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        })
      : items;
    return [...filtered].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [items, filter]);

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by SKU, name, service, device…"
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() => {
            setSaveError(null);
            setCreating(true);
          }}
        >
          Add repair service
        </ToolbarButton>
      </SecondaryToolbar>

      {/* Two-column layout — table left, edit panel right. Collapses to a
          single column when the panel is closed. */}
      <div
        className={`repair-services-layout ${
          dialogOpen
            ? 'repair-services-layout--panel-open'
            : 'repair-services-layout--panel-closed'
        }`}
      >
        <Box sx={{ p: '12px 16px 24px', minWidth: 0 }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell width={100}>SKU</DataTableHeadCell>
                  <DataTableHeadCell width={320}>Display name</DataTableHeadCell>
                  <DataTableHeadCell>Device</DataTableHeadCell>
                  <DataTableHeadCell>Service</DataTableHeadCell>
                  <DataTableHeadCell align="right">Default hrs</DataTableHeadCell>
                  <DataTableHeadCell align="center">Auto part</DataTableHeadCell>
                  <DataTableHeadCell>Active</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <DataTableEmpty colSpan={8}>
                    {filter.trim()
                      ? 'No repair services match your filter'
                      : 'No repair services configured'}
                  </DataTableEmpty>
                ) : (
                  sortedItems.map((item) => (
                    <DataTableRow key={item.id}>
                      <DataTableCell>{item.repairServiceCode || '—'}</DataTableCell>
                      <DataTableCell>{item.displayName || '—'}</DataTableCell>
                      <DataTableCell>{formatDeviceType(item.deviceType)}</DataTableCell>
                      <DataTableCell>{item.serviceType || '—'}</DataTableCell>
                      <DataTableCell align="right">
                        {item.defaultLabourUnits != null
                          ? item.defaultLabourUnits
                          : '—'}
                      </DataTableCell>
                      <DataTableCell align="center">
                        {(item.requiresPart ??
                          item.componentCategories.length > 0)
                          ? 'Yes'
                          : 'No'}
                      </DataTableCell>
                      <DataTableCell>{item.isActive ? 'Yes' : 'No'}</DataTableCell>
                      <DataTableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSaveError(null);
                              setEditing(item);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleting(item)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
        </Box>

        {dialogOpen && (
          <div className="repair-services-panel-col">
            <RepairServicePanel
              repairServiceType={editing}
              products={products}
              productsLoading={productsLoading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              isSaving={saving}
              error={saveError}
              onClose={closeDialog}
              onSave={handleSave}
            />
          </div>
        )}
      </div>

      <Dialog open={deleting !== null} onClose={() => !deleteSubmitting && setDeleting(null)}>
        <DialogTitle>Delete repair service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleting?.displayName || deleting?.repairServiceCode}</strong>?
            Existing quotes that already reference its SKU keep their saved
            line — only the picker entry is removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
